import type { ReactNode } from 'react'
import { Help } from '@/components/Form'
import {
  type InputValue,
  ValueInput,
} from '@/features/legends/filters/components/ValueInput'
import type { AnswerOption } from '@/features/legends/editor/entities'
import type { RynoType } from '@/features/legends/editor/ryno-types'
import {
  type Choice,
  ChoiceSelect,
} from '@/features/legends/components/ChoiceSelect'
import type { BinaryOperator } from '@/features/legends/filters/rules'
import styles from './BinaryOperationFilter.module.css'

export interface BinaryOperationFilterProps {
  /**
   * The symbol picker for what is compared.
   */
  target: ReactNode
  op: BinaryOperator
  ops: readonly Choice<BinaryOperator>[]
  onOpChange: (op: BinaryOperator) => unknown
  valueType: RynoType
  value: InputValue
  /**
   * The target's answers, when the value is picked from them.
   */
  valueOptions: AnswerOption[] | null
  onValueChange: (value: InputValue) => unknown
  targetError?: string
  opError?: string
  valueError?: string
  isDisabled?: boolean
  testId?: string
}

/**
 * Compares a symbol with a value: "[target] [is] [value]".
 */
export function BinaryOperationFilter({
  target,
  op,
  ops,
  onOpChange,
  valueType,
  value,
  valueOptions,
  onValueChange,
  targetError,
  opError,
  valueError,
  isDisabled,
  testId,
}: BinaryOperationFilterProps) {
  return (
    <div className={styles.root}>
      {target}
      <Help error={targetError} />
      <div className={styles.comparison}>
        <ChoiceSelect
          value={op}
          choices={ops}
          onChange={onOpChange}
          isDisabled={isDisabled}
          isInvalid={!!opError}
          aria-label="Operation"
          testId={testId && `${testId}-operation`}
        />
        <ValueInput
          type={valueType}
          value={value}
          onChange={onValueChange}
          options={valueOptions}
          isDisabled={isDisabled}
          isInvalid={!!valueError}
          testId={testId && `${testId}-value`}
        />
      </div>
      <Help error={opError} />
      <Help error={valueError} />
    </div>
  )
}
